import { SERVICE_PREFIX } from "@common/constants";
import {
    COUNTERPARTY_TIN_EXPORT_PATH,
    COUNTERPARTY_TIN_REVEAL_PATH,
    COUNTERPARTY_TIN_WRITE_PATH,
} from "@common/constants/counterpartyTin";
import { getEnv } from "@lib/environment";

export type VgsEnvironment = "sandbox" | "live";

export interface VgsConfig {
    vaultId: string;
    environment: VgsEnvironment;
    collectVersion: string;
    collectIntegrity: string;
    showVersion: string;
    showIntegrity: string;
    cname: string | null;
}

/** Must stay 4.x: `@vgs/collect-js-react` `VGSCollect.session` requires it. */
const VGS_COLLECT_VERSION = "4.0.1";
/**
 * SRI hashes of `vgs-collect/${VGS_COLLECT_VERSION}/vgs-collect.js` on the main
 * CDN and on the fallback CDN the loader retries (`js3`). The two builds embed
 * their own origin, so each needs its hash; update both with the version:
 * `for host in js js3; do curl -s https://$host.verygoodvault.com/vgs-collect/<version>/vgs-collect.js | openssl dgst -sha384 -binary | openssl base64 -A; echo; done`
 */
const VGS_COLLECT_INTEGRITY = [
    "sha384-Sr5xwyR0H5rcDXa7/iDJDM3qBBvtHuR97A8ELbd+DFBDi32Caf72to9UVqvI8R95",
    "sha384-MWkU66hcMD05kQZIsrfatn6A+djTJ3uRCwbPt9XRBmYGOGsk2YGHRZsV5716EuZz",
].join(" ");
const VGS_SHOW_VERSION = "2.2.3";
/**
 * SRI hash of `vgs-show/${VGS_SHOW_VERSION}/show.js`; update it with the version:
 * `curl -s <url> | openssl dgst -sha384 -binary | openssl base64 -A`
 */
const VGS_SHOW_INTEGRITY =
    "sha384-Cw0OQPNDho9sjvqMutQJx8sQHUl89G/RLCWsrFcr0WRQ0FFcPr2b5r7sminLYAUn";

const VGS_ENVIRONMENTS: readonly VgsEnvironment[] = ["sandbox", "live"];

/** VGS forwards the path to our backend verbatim, so it carries the `/api` prefix. */
export const VGS_TIN_WRITE_PATH = `/${SERVICE_PREFIX.MAIN_APP}${COUNTERPARTY_TIN_WRITE_PATH}`;
export const VGS_TIN_REVEAL_PATH = `/${SERVICE_PREFIX.MAIN_APP}${COUNTERPARTY_TIN_REVEAL_PATH}`;
export const VGS_TIN_EXPORT_PATH = `/${SERVICE_PREFIX.MAIN_APP}${COUNTERPARTY_TIN_EXPORT_PATH}`;

/** The inbound route Collect and Show post through, for requests made without them. */
export function getVgsProxyOrigin({
    vaultId,
    environment,
    cname,
}: VgsConfig): string {
    return cname
        ? `https://${cname}`
        : `https://${vaultId}.${environment}.verygoodproxy.com`;
}

function parseVgsEnvironment(value: string): VgsEnvironment | null {
    return (
        VGS_ENVIRONMENTS.find((environment) => environment === value) ?? null
    );
}

/** `null` until every required `VITE_VGS_*` variable is set. */
export function getVgsConfig(): VgsConfig | null {
    const vaultId = getEnv("VITE_VGS_VAULT_ID");
    const environment = parseVgsEnvironment(getEnv("VITE_VGS_ENVIRONMENT"));
    if (!vaultId || !environment) {
        return null;
    }
    return {
        vaultId,
        environment,
        collectVersion: VGS_COLLECT_VERSION,
        collectIntegrity: VGS_COLLECT_INTEGRITY,
        showVersion: VGS_SHOW_VERSION,
        showIntegrity: VGS_SHOW_INTEGRITY,
        cname: getEnv("VITE_VGS_CNAME") || null,
    };
}
